import { CraneAction, KeyMapping } from "../types";

// Type declaration for HMR
declare const module: {
  hot?: {
    dispose: (callback: () => void) => void;
    accept: (callback: () => void) => void;
  };
};

class KeyboardService {
  private isEnabled: boolean = false;
  private actionHandler?: (action: CraneAction) => void;
  private keyMappings: KeyMapping[] = [];
  private isHMR: boolean = false;
  private rawKeyHandlers: Array<(code: string, event: KeyboardEvent) => void> =
    [];

  constructor() {
    // Handle HMR cleanup
    if (typeof module !== "undefined" && module.hot) {
      module.hot.dispose(() => {
        this.disable();
        this.isHMR = true;
      });

      module.hot.accept(() => {
        if (this.isHMR) {
          this.isHMR = false;
          // Re-enable if it was active before HMR
          if (this.isEnabled) {
            this.enable();
          }
        }
      });
    }
  }

  enable() {
    if (this.isEnabled) {
      this.disable();
    }

    this.isEnabled = true;
    window.addEventListener("keydown", this.handleKeyDown);

    // Cleanup on page unload
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  }

  disable() {
    if (!this.isEnabled) return;

    this.isEnabled = false;
    this.actionHandler = undefined;
    window.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  }

  // Register action handler (called by store)
  onAction(handler: (action: CraneAction) => void) {
    this.actionHandler = handler;
  }

  // Register key mappings
  registerMappings(mappings: KeyMapping[]) {
    this.keyMappings = mappings;
  }

  // Register raw key handler (for non-mapped features)
  onRawKey(handler: (code: string, event: KeyboardEvent) => void) {
    this.rawKeyHandlers.push(handler);
    return () => {
      this.rawKeyHandlers = this.rawKeyHandlers.filter((h) => h !== handler);
    };
  }

  private handleKeyDown = (event: KeyboardEvent) => {
    if (!this.isEnabled) return;

    // Prevent default behavior for control keys
    if (
      ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Space"].includes(
        event.code
      )
    ) {
      event.preventDefault();
    }

    // Skip if typing in input fields
    if (
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement
    ) {
      return;
    }

    const keyCode = this.getKeyCode(event);
    console.log("keyCode", keyCode);
    const mapping = this.keyMappings.find((m) => m.key === keyCode);
    if (mapping && this.actionHandler) {
      this.actionHandler(mapping.action);
    }

    if (this.rawKeyHandlers.length > 0) {
      // Call all listeners; stop if any prevented default explicitly
      for (const handler of this.rawKeyHandlers) {
        try {
          handler(keyCode, event);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("keyboardService rawKey handler error", err);
        }
      }
    }
  };

  private handleBeforeUnload = () => {
    this.disable();
  };

  private getKeyCode(event: KeyboardEvent): string {
    const modifiers = [];
    if (event.shiftKey) modifiers.push("Shift");
    if (event.ctrlKey) modifiers.push("Ctrl");
    if (event.altKey) modifiers.push("Alt");

    const key = event.code;
    return modifiers.length > 0 ? `${modifiers.join("+")}+${key}` : key;
  }

  // Get all registered mappings (for help/UI)
  getMappings(): KeyMapping[] {
    return [...this.keyMappings];
  }

  // Check if a key is mapped
  isKeyMapped(key: string): boolean {
    return this.keyMappings.some((m) => m.key === key);
  }
}

// Export singleton
export const keyboardService = new KeyboardService();

// Pre-configured key mappings for crane actions
export const CRANE_KEY_MAPPINGS: KeyMapping[] = [
  // Boom speed controls (Up/Down arrows)
  {
    key: "ArrowUp",
    action: { type: "increment", parameter: "boomSpeed", amount: 5 },
    description: "Increase boom speed",
  },
  {
    key: "ArrowDown",
    action: { type: "increment", parameter: "boomSpeed", amount: -5 },
    description: "Decrease boom speed",
  },

  // Speed controls (Shift + arrows)
  {
    key: "Shift+ArrowUp",
    action: { type: "increment", parameter: "speed", amount: 5 },
    description: "Increase speed",
  },
  {
    key: "Shift+ArrowDown",
    action: { type: "increment", parameter: "speed", amount: -5 },
    description: "Decrease speed",
  },
  // Slew speed controls (Left/Right arrows)
  {
    key: "ArrowLeft",
    action: { type: "increment", parameter: "slewSpeed", amount: -5 },
    description: "Slew left faster",
  },
  {
    key: "ArrowRight",
    action: { type: "increment", parameter: "slewSpeed", amount: 5 },
    description: "Slew right faster",
  },
  // Load controls
  {
    key: "KeyL",
    action: { type: "increment", parameter: "mhLoad", amount: 50 },
    description: "Increase MH load",
  },
  {
    key: "KeyK",
    action: { type: "increment", parameter: "mhLoad", amount: -50 },
    description: "Decrease MH load",
  },

  // Special actions
  {
    key: "Space",
    action: { type: "set", parameter: "speed", value: 0 },
    description: "Set speed to 0",
  },
  {
    key: "Digit0",
    action: { type: "set", parameter: "speed", value: 25 },
    description: "Reset speed to default",
  },
];

// Helper functions
export const startKeyboardService = () => {
  keyboardService.registerMappings(CRANE_KEY_MAPPINGS);
  keyboardService.enable();
};

export const stopKeyboardService = () => {
  keyboardService.disable();
};
